import { Checkbox, Divider, Form, Input, Select, Spin, Tooltip } from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MaskInput } from "shared/components";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";
import { pixKeyTypeList } from "shared/enums/PixKeyTypeEnum";
import { formCNPJValidator } from "shared/helpers/cnpj-validator";
import { formCPFValidatorSimple } from "shared/helpers/cpf-validator";
import useFetchData from "shared/hooks/useFetchData";
import { ReactComponent as Logo } from "shared/assets/images/contrato-icon.svg";
import { ReactComponent as IconDiamond } from "shared/assets/images/icon-diamante-selo.svg";
import { ReactComponent as WarningLogo } from "shared/assets/images/icon-contract-warning.svg";
import {
  fecthBankList,
  getAddressByCep,
  getDistributionContractSpecialCampaign,
  postSignDistributionContract,
  postSignDistributionSpecialCampaignContract,
} from "shared/service/StrmService";
import "./Distribua.scss";
import "../adiantamentos/Adiantamentos.scss";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { SpecialCampaignEnum } from "shared/enums/SpecialCampaignEnum";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { DistroButtonClickEventName } from "event-tracker/button-click-event-name";
import { ArtistState, getLoggedUserArtist } from "explorer/reducers/artistas";
import { documentTypeList } from "shared/enums/DocumentTypeEnum";
import { bankAccountTypeList } from "shared/enums/BankAccountType";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { getMixPanelParams } from "shared/helpers/cookie-helpers";
import { getLoggedUser, UserState } from "explorer/reducers/users";
import { DistributionStatusEnum } from "shared/enums/DistributionStatusEnum";
import Duvidas from "../adiantamentos/etapas/Duvidas";
import logger from "shared/service/LoggerService";
import { TrimmedInput } from "shared/components/trimmed-input/TrimmedInput";
import InputUsd from "shared/components/input-usd/InputUsd";
import imgAnnoDominiLogoDistro from "shared/assets/images/anno-domini-logo-distro.png";

enum TipoPessoa {
  Fisica = 0,
  Juridica = 1,
}

enum StepPage {
  Form = 0,
  Terms = 1,
  Contract = 2,
  Success = 3,
}

const BankOtherCode = "000";

const CAMPANHAS_ESPECIAIS = [
  SpecialCampaignEnum.HenriqueEJuliano,
  SpecialCampaignEnum.KamillaFialho,
  SpecialCampaignEnum.AnnoDomini,
];

const Distribua: React.FC<any> = (props) => {
  const { location } = props;
  const state = location.state;
  const { specialCampaign } = state || {};
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [termsOfUseHtml, setTermsOfUseHtml] = useState<string | null>(null);
  const [step, setStep] = useState<StepPage>(StepPage.Form);
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>(TipoPessoa.Fisica);
  const [contratoParaAssinar, setContratoParaAssinar] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [checkBoxCheckeds, setCheckBoxCheckeds] = useState<any>({
    readAndUnderstood: false,
    acceptTerms: false,
  });
  const history = useHistory();
  const { loggedUser } = useSelector((state: UserState) => state.users);
  const { userArtist, loading: artistLoading } = useSelector(
    (state: ArtistState) => state.artists,
  );
  const internationalArtist = userArtist.country !== "BR";
  const bankCode = Form.useWatch("bankCode", form);

  const [bankOptions, bankLoading] = useFetchData(
    fecthBankList,
    [],
    useCallback(
      (data: any) => {
        const response = data;
        const mappedData = response.map((item: any) => ({
          ...item,
          label: `${item?.value} - ${item?.label}`,
        }));

        return [
          { value: BankOtherCode, label: t("pages.Adiantamentos.outroBanco") },
          ...mappedData,
        ];
      },
      [t],
    ),
  );

  const bankAccountTypeOptions = useMemo(
    () => [
      ...bankAccountTypeList.map(({ label, value }) => ({
        label: t(`pages.Adiantamentos.${label}`),
        value,
      })),
    ],
    [t],
  );

  const getContratoAtual = useCallback(async () => {
    try {
      const result: any = await getDistributionContractSpecialCampaign();

      if (!result?.response || result?.response === null) {
        return;
      }
      setContratoParaAssinar(result?.response?.signUrl);
      setStep(StepPage.Contract);
    } catch (error) {
      logger.error(error, {
        transaction: "distribution_contract",
        tags: { flow: "distro_sign" },
      });
    }
  }, []);

  const onClickAssinar = async () => {
    if (contratoParaAssinar) {
      logger.message("User attempting to sign contract", {
        transaction: "distribution_contract",
        tags: { flow: "distro_sign" },
        category: "contract_sign_attempt",
      });

      const newWindow = window.open(
        contratoParaAssinar,
        "_blank",
        "noopener,noreferrer",
      );
      if (newWindow) newWindow.opener = null;
    } else {
      logger.error(new Error("Contract URL not available"), {
        transaction: "distribution_contract",
        tags: { flow: "distro_sign" },
      });
      toast.error<string>(t("pages.Adiantamentos.erroAssinarContrato"));
    }
  };

  const onClickHome = (reloadUser: boolean = false) => {
    if (reloadUser) {
      dispatch(getLoggedUserArtist());
      dispatch(getLoggedUser());
      // dispatch(getUserDistroToken());
    }
    history.push("/");
  };

  const onFinish = async () => {
    if (sending) return;

    setSending(true);

    if (CAMPANHAS_ESPECIAIS.includes(specialCampaign)) {
      const values = form.getFieldsValue(true);

      const request = {
        ...values,
        specialCampaign,
      };

      try {
        logger.message("Starting special campaign contract generation", {
          transaction: "distribution_contract",
          tags: { flow: "distro_sign", type: "special_campaign" },
          category: "special_campaign_contract_start",
          context: {
            userArtistId: userArtist?.id,
            specialCampaign,
          },
        });

        await postSignDistributionSpecialCampaignContract(request);
        await getContratoAtual();
        setStep(StepPage.Contract);
      } catch (error) {
        logger.error(error, {
          transaction: "distribution_contract",
          tags: { flow: "distro_sign", type: "special_campaign" },
        });
        toast.error<string>(t("pages.Distribua.erroCriacaoContrato"));
      }
    } else {
      let docFile = "";
      if (internationalArtist) {
        docFile = "simplificado_adesao_internacional_template_strm.html";
      } else {
        docFile =
          tipoPessoa === TipoPessoa.Fisica
            ? "simplificado_adesao_pf_template_strm distro.html"
            : "simplificado_adesao_pj_template_strm distro.html";
      }

      setStep(StepPage.Terms);
      fetch(docFile)
        .then((response) => response.text())
        .then((html) => {
          setTermsOfUseHtml(html);
        })
        .catch((error) => {
          logger.error(error, {
            transaction: "distribution_contract",
            tags: { flow: "distro_sign" },
          });
          console.error("There was an error loading the terms of use:", error);
        });
    }

    setSending(false);
  };

  const sendTagManagerEventContractSigned = (logContextSentry: any) => {
    try {
      dataLayerClickedEvent(DistroButtonClickEventName.SIGN_CONTRACT, {
        ...tagManagerEvent.Distro.S4A_DISTRO_SIGN,
        approvedLimit: userArtist?.cashAdvanceEstimated?.amountUsd,
        alreadyCalculated:
          userArtist?.cashAdvanceEstimated?.successfullyEstimated,
        carreerStage: userArtist.stage,
      });
    } catch (error) {
      logger.error(error, logContextSentry);
    }
  };

  const onClickAceitar = async () => {
    setLoading(true);
    let request = {};

    await logger.initializeUserContext();
    const logContextSentry: any = {
      transaction: "distribution_contract",
      tags: { flow: "distro_sign" },
      context: {
        userArtistId: userArtist?.id,
      },
    };

    try {
      sendTagManagerEventContractSigned(logContextSentry);

      request = {
        ...form.getFieldsValue(true),
        userArtistId: userArtist.id,
        utmTrackingData: getMixPanelParams(),
      };

      logger.message("Sending contract signature request", {
        ...logContextSentry,
        category: "contract_sign_request",
      });

      const response = await postSignDistributionContract(request);

      logContextSentry.context = {
        ...logContextSentry.context,
        response,
      };

      if (response) {
        logger.message("Contract signed successfully", {
          ...logContextSentry,
          category: "contract_sign_success",
        });
        dispatch(getLoggedUserArtist());
        dispatch(getLoggedUser());
        // dispatch(getUserDistroToken());
        setStep(StepPage.Success);
      } else {
        logContextSentry.context = {
          ...logContextSentry.context,
          request: JSON.stringify(request),
        };
        logger.error(
          new Error("Backend response was null or false"),
          logContextSentry,
        );
      }
    } catch (error) {
      logContextSentry.context = {
        ...logContextSentry.context,
        request: JSON.stringify(request),
      };
      logger.fatal(error, logContextSentry);
    } finally {
      setLoading(false);
    }
  };

  const onCheckboxChange = (event: CheckboxChangeEvent) => {
    const { name = "", checked } = event.target;
    setCheckBoxCheckeds((prevState: any) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const onClickSubmitForm = () => {
    form.submit();
    dataLayerClickedEvent(DistroButtonClickEventName.NEXT, {
      ...tagManagerEvent.Distro.S4A_DISTRO_FORM,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    getContratoAtual();
  }, [getContratoAtual]);

  useEffect(() => {
    if (step === StepPage.Success) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Distro.S4A_DISTRO_SIGNATURE_CONFIRMATION,
        carreerStage: userArtist.stage,
        country: userArtist.country,
        userTest: userArtist.internalTest,
      });
    }
  }, [step, userArtist]);

  if (loading || artistLoading) {
    return (
      <div
        className={`adiantamentos ${!isMobile && "web"}`}
        style={{ flex: 1 }}
      >
        <div className="flex-column-between">
          <div
            className="migracao"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <>
              <div className="confirma-adiantamento-intro">
                <Logo />
                <h1>{t("pages.Adiantamentos.carregando")}</h1>
                <div
                  style={{ maxWidth: isMobile ? "unset" : 650, margin: "12px" }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined
                        spin={false}
                        style={{ color: "#5779ff" }}
                      />
                    }
                    size="large"
                  />
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexDirection: "column",
                }}
                className="footer black"
              >
                <Duvidas />
                <button
                  className="botao-borda-roxa"
                  onClick={() => onClickHome()}
                >
                  {t("pages.Adiantamentos.botaoHome")}
                </button>
              </div>
            </>
          </div>
        </div>
      </div>
    );
  }

  if (userArtist?.distribution?.status === DistributionStatusEnum.Pending) {
    return (
      <div className={`adiantamentos ${!isMobile && "web"}`}>
        <div
          className="flex-column-between"
          style={{
            minHeight: "calc(100dvh - 64px)",
          }}
        >
          <div
            className="migracao"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <>
              <div className="confirma-adiantamento-intro">
                <WarningLogo />
                <h1 style={{ color: "#F4D566", margin: "12px 0" }}>
                  {t("pages.Adiantamentos.confeccaoContratoTitulo")}
                </h1>
                <p style={{ maxWidth: isMobile ? "unset" : 650 }}>
                  <Trans
                    i18nKey={t("pages.Adiantamentos.confeccaoContratoTexto")}
                  />
                </p>
              </div>
              <div
                style={{
                  flex: 1,
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexDirection: "column",
                }}
                className="footer black"
              >
                <Duvidas />
                <button
                  className="botao-borda-roxa"
                  onClick={() => onClickHome()}
                >
                  {t("pages.Adiantamentos.botaoHome")}
                </button>
              </div>
            </>
          </div>
        </div>
      </div>
    );
  }

  if (step === StepPage.Contract) {
    return (
      <div className={`adiantamentos ${!isMobile && "web"}`}>
        <div
          className="flex-column-between"
          style={{
            minHeight: isMobile ? "calc(100vh - 64px)" : "100vh",
            paddingLeft: isMobile ? 0 : 94,
          }}
        >
          <div
            className="migracao"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="confirma-adiantamento-intro">
              <Logo />
              <h1>{t("pages.Adiantamentos.contratoEnviadoTitulo")}</h1>
              <p style={{ maxWidth: isMobile ? "unset" : 650 }}>
                <Trans
                  i18nKey={t("pages.Adiantamentos.contratoEnviadoTexto")}
                />
              </p>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <button
                className="botao-azul"
                onClick={onClickAssinar}
                data-testid="btn-assinar-contrato"
              >
                {t("pages.Adiantamentos.assinar")}
              </button>
              <button
                className="botao-borda-roxa"
                onClick={() => onClickHome(true)}
              >
                {t("pages.Distribua.assinadoVoltar")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === StepPage.Terms)
    return (
      <div className={`adiantamentos ${!isMobile && "web"}`}>
        <div
          className="contrato-assinatura distribua"
          style={{
            marginLeft: isMobile ? "unset" : 80,
            minHeight: isMobile ? "calc(100vh - 64px)" : "100vh",
          }}
          data-testid="terms-step"
        >
          <div className="flex-column-between">
            <div className="migracao envio-relatorio">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                  style={{
                    maxWidth: isMobile ? "unset" : 650,
                    backgroundColor: "#fff",
                    color: "#000",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: termsOfUseHtml || "Loading...",
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <Form.Item label={t("pages.Distribua.liCompreendi")}>
                <Checkbox
                  name="readAndUnderstood"
                  checked={checkBoxCheckeds.readAndUnderstood}
                  onChange={onCheckboxChange}
                  data-testid="cb-read-terms"
                />
              </Form.Item>
              <Form.Item label={t("pages.Distribua.aceitoTermos")}>
                <Checkbox
                  name="acceptTerms"
                  checked={checkBoxCheckeds.acceptTerms}
                  onChange={onCheckboxChange}
                  data-testid="cb-accept-terms"
                />
              </Form.Item>
            </div>
            <div className="footer black" style={{ paddingLeft: 8 }}>
              <button
                className="botao-roxo"
                onClick={() => setStep(StepPage.Form)}
              >
                {t("pages.Adiantamentos.recusar")}
              </button>
              <button
                disabled={
                  !(
                    checkBoxCheckeds.readAndUnderstood &&
                    checkBoxCheckeds.acceptTerms
                  ) || loading
                }
                className="botao-roxo"
                onClick={onClickAceitar}
                data-testid="btn-aceitar-termos"
              >
                {t("pages.Adiantamentos.aceitar")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  if (step === StepPage.Success)
    return (
      <div className={`adiantamentos ${!isMobile && "web"}`}>
        <div
          className="flex-column-between contrato-assinatura"
          style={{
            minHeight: isMobile ? "calc(100vh - 64px)" : "100vh",
            paddingLeft: isMobile ? 0 : 94,
          }}
          data-testid="success-step"
        >
          <div
            className="migracao"
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <>
              <div className="confirma-adiantamento-intro">
                <Logo />
                <h1 className="title">
                  {t("pages.Distribua.contratoAssinadoTitulo")}
                </h1>
                <p
                  style={{ maxWidth: isMobile ? "unset" : 650 }}
                  className="texto"
                >
                  <>
                    <Trans
                      i18nKey={t("pages.Distribua.contratoAssinadoTexto")}
                    />
                  </>
                </p>
              </div>
            </>
          </div>

          <div className="footer" style={{ paddingLeft: 8 }}>
            <button
              className="botao-borda-roxa"
              onClick={() => history.push("/estagio?distro=assinado")}
            >
              {t("pages.Adiantamentos.botaoHome")}
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className={`adiantamentos ${!isMobile && "web"}`}>
      <div
        className="contrato-assinatura distribua"
        style={{
          marginLeft: isMobile ? "unset" : 80,
          minHeight: isMobile ? "calc(100vh - 64px)" : "100vh",
        }}
      >
        <div className="flex-column-between">
          <div className="migracao envio-relatorio">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  maxWidth: isMobile ? "unset" : 650,
                }}
              >
                {specialCampaign === SpecialCampaignEnum.AnnoDomini && (
                  <div className="campanha-especial-container">
                    <div>
                      <img
                        className="logo-image"
                        src={imgAnnoDominiLogoDistro}
                        alt="Anno Domini"
                      />
                    </div>
                    <div className="info-container">
                      <div className="digital-label-badge">
                        <IconDiamond /> {t("pages.Distribua.digitalLabel")}
                      </div>
                      <p className="campaign-title">Ano Domini Nation</p>
                    </div>
                  </div>
                )}
                <div className="confirma-adiantamento-intro">
                  <h1>{t("pages.Distribua.parabensTitulo")}</h1>
                  <p>
                    <Trans
                      i18nKey={t("pages.Distribua.parabensTexto", {
                        estagio: userArtist.stage,
                      })}
                    />
                  </p>
                </div>
                <Divider />

                <Form
                  form={form}
                  onFinish={onFinish}
                  className="contrato"
                  layout="vertical"
                  initialValues={{
                    fiscalType: TipoPessoa.Fisica,
                    email: loggedUser.login,
                  }}
                >
                  <h2>
                    {t("pages.Adiantamentos.confirmaAdiantamentoTituloForm")}
                  </h2>
                  {!internationalArtist && (
                    <>
                      <span className="sub-titulo">
                        {t("pages.Adiantamentos.pessoaFisicaJuridica")}
                      </span>
                      <Form.Item
                        name="fiscalType"
                        label={t("pages.Adiantamentos.tipoPessoa")}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "pages.Adiantamentos.tipoPessoaRequired",
                            ),
                          },
                        ]}
                      >
                        <Select onChange={(value) => setTipoPessoa(value)}>
                          <Select.Option value={TipoPessoa.Fisica}>
                            {t("pages.Adiantamentos.pessoaFisica")}
                          </Select.Option>
                          <Select.Option value={TipoPessoa.Juridica}>
                            {t("pages.Adiantamentos.pessoaJuridica")}
                          </Select.Option>
                        </Select>
                      </Form.Item>
                      {tipoPessoa === TipoPessoa.Juridica && (
                        <>
                          <PessoaJuridicaForm />
                        </>
                      )}
                    </>
                  )}
                  <PessoaFisicaForm
                    titulo={
                      tipoPessoa === TipoPessoa.Fisica
                        ? t("pages.Adiantamentos.confirmaAdiantamentoFisica")
                        : t(
                            "pages.Adiantamentos.confirmaAdiantamentoFisicaRepresentante",
                          )
                    }
                  />
                  <span className="sub-titulo">
                    {t("pages.Adiantamentos.dadosBancarios")}
                  </span>
                  <Form.Item
                    name="bankAccountName"
                    label={t("pages.Adiantamentos.bankAccountName")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "pages.Adiantamentos.bankAccountNameRequired",
                        ),
                      },
                    ]}
                  >
                    <Input data-testid="input-bank-account-name" />
                  </Form.Item>
                  {!internationalArtist && (
                    <>
                      <Form.Item
                        name="pixKeyType"
                        label={t("pages.Adiantamentos.tipoChavePix")}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "pages.Adiantamentos.tipoChavePixRequired",
                            ),
                          },
                        ]}
                      >
                        <Select
                          options={pixKeyTypeList.map((item) => ({
                            ...item,
                            label: t(
                              `pages.Adiantamentos.${item.label.toLocaleLowerCase()}`,
                            ),
                          }))}
                          data-testid="select-pix-type"
                        />
                      </Form.Item>
                      <Form.Item
                        name="pixKey"
                        label={t("pages.Adiantamentos.chavePix")}
                        rules={[
                          {
                            required: true,
                            message: t("pages.Adiantamentos.chavePixRequired"),
                          },
                        ]}
                      >
                        <Input maxLength={100} data-testid="input-pix-key" />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item
                    name="bankCode"
                    label={t("pages.Adiantamentos.codigoBanco")}
                    rules={[
                      {
                        required: true,
                        message: t("pages.Adiantamentos.codigoBancoRequired"),
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      options={bankOptions}
                      loading={bankLoading}
                      filterOption={filterOptionDiacritics}
                      data-testid="select-bank-code"
                    />
                  </Form.Item>{" "}
                  {bankCode === BankOtherCode && (
                    <Form.Item
                      name="bankName"
                      label={t("pages.Adiantamentos.nomeOutroBanco")}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "pages.Adiantamentos.nomeOutroBancoRequired",
                          ),
                        },
                      ]}
                    >
                      <Input maxLength={100} />
                    </Form.Item>
                  )}
                  {!internationalArtist ? (
                    <>
                      <Form.Item
                        name="bankAgency"
                        label={t("pages.Adiantamentos.agenciaBanco")}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "pages.Adiantamentos.agenciaBancoRequired",
                            ),
                          },
                        ]}
                      >
                        <Input data-testid="input-bank-agency" />
                      </Form.Item>{" "}
                    </>
                  ) : (
                    <>
                      <Form.Item
                        name="bankRoutingCode"
                        label={
                          <>
                            <span className="label-bank-routing-code">
                              {t("pages.Adiantamentos.bankRoutingCode")}
                            </span>
                            <Tooltip
                              title={t(
                                "pages.Adiantamentos.bankRoutingDisclaimer",
                              )}
                            >
                              <InfoCircleOutlined />
                            </Tooltip>
                          </>
                        }
                        rules={[
                          {
                            required: true,
                            message: t(
                              "pages.Adiantamentos.bankRoutingCodeRequired",
                            ),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>{" "}
                    </>
                  )}
                  <Form.Item
                    name="bankAccountNumber"
                    label={t("pages.Adiantamentos.numeroContaBanco")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "pages.Adiantamentos.numeroContaBancoRequired",
                        ),
                      },
                    ]}
                  >
                    <Input data-testid="input-bank-account" />
                  </Form.Item>
                  {internationalArtist && (
                    <>
                      <Form.Item
                        name="bankAccountType"
                        label={t("pages.Adiantamentos.bankAccountType")}
                        rules={[
                          {
                            required: true,
                            message: t(
                              "pages.Adiantamentos.bankAccountTypeRequired",
                            ),
                          },
                        ]}
                      >
                        <Select options={bankAccountTypeOptions} />
                      </Form.Item>{" "}
                    </>
                  )}
                  <Form.Item
                    name="artistStreamingRoyaltiesEstimated"
                    label={t(
                      "pages.Adiantamentos.estimativaArtistaRoyaltiesStreaming",
                    )}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "pages.Adiantamentos.estimativaArtistaRoyaltiesStreamingRequired",
                        ),
                      },
                    ]}
                  >
                    <InputUsd data-testid="input-estimated-royalties" />
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>

          <div className="footer black" style={{ paddingLeft: 8 }}>
            <button className="botao-roxo" onClick={() => history.push("/")}>
              {t("pages.Adiantamentos.voltar")}
            </button>
            <button
              className="botao-roxo continuar"
              onClick={onClickSubmitForm}
              data-testid="btn-continuar"
              disabled={sending}
            >
              {t("pages.Adiantamentos.continuar")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PessoaFisicaForm: React.FC<any> = (props) => {
  const { titulo } = props;
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [loadingCEP, setLoadingCEP] = useState<boolean>(false);
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const documentTypeOptions = useMemo(
    () => [
      ...documentTypeList.map(({ label, value }) => ({
        label: t(`pages.Adiantamentos.${label}`),
        value,
      })),
    ],
    [t],
  );
  const internationalArtist = userArtist.country !== "BR";

  const fillAddressByCep = async (cep: string) => {
    try {
      if (cep) {
        setLoadingCEP(true);
        const response: any = await getAddressByCep(cep);
        form.setFieldsValue({
          street: response.street,
          state: response.uf,
          city: response.city,
        });
      }
    } finally {
      setLoadingCEP(false);
    }
  };
  return (
    <>
      <span className="sub-titulo">{titulo}</span>
      <Form.Item
        name="name"
        label={t("pages.Adiantamentos.nomeCompleto")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.nomeCompletoRequired"),
          },
          {
            pattern: /^\S+(\s+\S+)+$/,
            message: t("pages.Adiantamentos.nomeSobrenomeValidation"),
          },
        ]}
      >
        <TrimmedInput
          placeholder={t("pages.Adiantamentos.nomeCompletoPlaceholder")}
          data-testid="input-name"
        />
      </Form.Item>
      <Form.Item
        name="email"
        label={t("pages.Adiantamentos.email")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.emailRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.emailPlaceholder")}
          disabled
        />
      </Form.Item>
      {!internationalArtist ? (
        <>
          <Form.Item
            name="cpf"
            label={t("pages.Adiantamentos.cpf")}
            rules={[
              {
                required: true,
                message: t("pages.Adiantamentos.cpfRequired"),
              },
              ({ getFieldValue }) => ({
                validator() {
                  return formCPFValidatorSimple(getFieldValue, "cpf", t);
                },
              }),
            ]}
            validateTrigger={["onBlur", "onSubmit"]}
          >
            <MaskInput
              mask="999.999.999-99"
              placeholder={t("pages.Adiantamentos.cpfPlaceholder")}
              data-testid="input-cpf"
            />
          </Form.Item>
          <Form.Item
            name="rg"
            label={t("pages.Adiantamentos.rg")}
            rules={[
              {
                required: true,
                message: t("pages.Adiantamentos.rgRequired"),
              },
            ]}
          >
            <Input
              placeholder={t("pages.Adiantamentos.rgPlaceholder")}
              data-testid="input-rg"
            />
          </Form.Item>
          <Form.Item
            name="rgPlaceExpedition"
            label={t("pages.Adiantamentos.rgPlaceExpedition")}
            rules={[
              {
                required: true,
                message: t("pages.Adiantamentos.rgPlaceExpeditionRequired"),
              },
            ]}
          >
            <Input
              placeholder={t(
                "pages.Adiantamentos.rgPlaceExpeditionPlaceholder",
              )}
              data-testid="input-rg-place"
            />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            name="documentType"
            label={t("pages.Adiantamentos.idType")}
            rules={[
              {
                required: true,
                message: t("pages.Adiantamentos.idTypeRequired"),
              },
            ]}
          >
            <Select options={documentTypeOptions} />
          </Form.Item>
          <Form.Item
            name="documentNumber"
            label={t("pages.Adiantamentos.documentNumber")}
            rules={[
              {
                required: true,
                message: t("pages.Adiantamentos.documentNumberRequired"),
              },
            ]}
          >
            <Input placeholder={t("pages.Adiantamentos.idTypePlaceholder")} />
          </Form.Item>
        </>
      )}
      <span className="sub-titulo">
        {t("pages.Adiantamentos.confirmaAdiantamentoEndereco")}
      </span>
      <Form.Item
        name="cep"
        label={t("pages.Adiantamentos.cep")}
        hasFeedback={loadingCEP}
        validateStatus={loadingCEP ? "validating" : undefined}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.cepRequired"),
          },
        ]}
      >
        <Input
          onChange={debounce(
            (event) => fillAddressByCep(event.target.value),
            2000,
          )}
          placeholder={t("pages.Adiantamentos.cepPlaceholder")}
          maxLength={8}
          data-testid="input-cep"
        />
      </Form.Item>
      <Form.Item
        name="street"
        label={t("pages.Adiantamentos.rua")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.ruaRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.ruaPlaceholder")}
          data-testid="input-street"
        />
      </Form.Item>

      <Form.Item
        name="addressComplement"
        label={t("pages.Adiantamentos.complemento")}
      >
        <Input placeholder={t("pages.Adiantamentos.complementoPlaceholder")} />
      </Form.Item>

      <Form.Item
        name="state"
        label={t("pages.Adiantamentos.estado")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.estadoRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.estadoPlaceholder")}
          data-testid="input-state"
        />
      </Form.Item>
      <Form.Item
        name="city"
        label={t("pages.Adiantamentos.cidade")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.cidadeRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.cidadePlaceholder")}
          data-testid="input-city"
        />
      </Form.Item>
    </>
  );
};

const PessoaJuridicaForm: React.FC<any> = () => {
  const [loadingCEP, setLoadingCEP] = useState<boolean>(false);
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const fillAddressByCep = async (cep: string) => {
    try {
      if (cep) {
        setLoadingCEP(true);

        const response: any = await getAddressByCep(cep);
        form.setFieldsValue({
          companyStreet: response.street,
          companyState: response.uf,
          companyCity: response.city,
        });
      }
    } finally {
      setLoadingCEP(false);
    }
  };
  return (
    <>
      <span className="sub-titulo">
        {t("pages.Adiantamentos.confirmaAdiantamentoJuridica")}
      </span>
      <Form.Item
        name="businessName"
        label={t("pages.Adiantamentos.razaoSocial")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.razaoSocialRequired"),
          },
        ]}
      >
        <Input placeholder={t("pages.Adiantamentos.razaoSocialPlaceholder")} />
      </Form.Item>

      <Form.Item
        name="cnpj"
        label={t("pages.Adiantamentos.cnpj")}
        // extra={t("pages.Adiantamentos.cpfExtra")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.cnpjRequired"),
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              return formCNPJValidator(value, getFieldValue, "cnpj", t);
            },
          }),
        ]}
      >
        <MaskInput
          mask="99.999.999/9999-99"
          placeholder={t("pages.Adiantamentos.cnpjPlaceholder")}
        />
      </Form.Item>
      <span className="sub-titulo">
        {t("pages.Adiantamentos.confirmaAdiantamentoEndereco")}
      </span>
      <Form.Item
        name="companyCEP"
        label={t("pages.Adiantamentos.cep")}
        hasFeedback={loadingCEP}
        validateStatus={loadingCEP ? "validating" : undefined}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.cepRequired"),
          },
        ]}
      >
        <Input
          onChange={debounce(
            (event) => fillAddressByCep(event.target.value),
            2000,
          )}
          placeholder={t("pages.Adiantamentos.cepPlaceholder")}
          maxLength={8}
        />
      </Form.Item>
      <Form.Item
        name="companyStreet"
        label={t("pages.Adiantamentos.rua")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.ruaRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.ruaPlaceholder")}
          data-testid="input-company-street"
        />
      </Form.Item>

      <Form.Item
        name="companyAddressComplement"
        label={t("pages.Adiantamentos.complemento")}
      >
        <Input
          placeholder={t("pages.Adiantamentos.complementoPlaceholder")}
          data-testid="input-company-address-complement"
        />
      </Form.Item>

      <Form.Item
        name="companyState"
        label={t("pages.Adiantamentos.estado")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.estadoRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.estadoPlaceholder")}
          data-testid="input-company-state"
        />
      </Form.Item>
      <Form.Item
        name="companyCity"
        label={t("pages.Adiantamentos.cidade")}
        rules={[
          {
            required: true,
            message: t("pages.Adiantamentos.cidadeRequired"),
          },
        ]}
      >
        <Input
          placeholder={t("pages.Adiantamentos.cidadePlaceholder")}
          data-testid="input-company-city"
        />
      </Form.Item>
    </>
  );
};

export default Distribua;
